import { DetailCard, DetailCardLink, type DetailCardRow } from '@civilgrid/ui/components';
import type React from 'react';
import { useSnapshot } from 'valtio';
import { formatRelativeAge } from '~/common/dateUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import store from '../store';
import { NOT_AVAILABLE } from '.';
import { getLinkHost, getPdfFileName, toAbsoluteHttpUrl } from './detailCardHelpers';

// all of these property values come from the agency (citygrid)
// rather than each feature, hence why they are ~special~
const agencyProperties: string[] = ['pdf_link', 'agency_link', 'outreach_email', 'outreach_email_name', 'notes'];

interface FeatureDetailCardProps {
  featureId: string;
  properties?: { [key: string]: any } | null;
  agencyId?: string;
  columns: string[];
  columnProperties: string[];
}

/**
 * The "more info" card for an intelligence table row: feature properties plus
 * agency-level contact/document details, rendered as label/value rows (PSCO-39).
 */
export default function FeatureDetailCard({
  featureId,
  properties,
  agencyId,
  columns,
  columnProperties,
}: FeatureDetailCardProps) {
  const { citygrid, project } = useSnapshot(store);

  const agency = agencyId ? citygrid[agencyId] : undefined;

  const agencyValue = (property: string): React.ReactNode => {
    const raw = agencyId && agency?.properties ? agency.properties[property]?.toString()?.trim() : undefined;

    if (!raw || raw === NOT_AVAILABLE) {
      return undefined;
    }

    if (property === 'pdf_link') {
      const isSignedPDF: boolean | undefined = agency?.properties?.isSignedPDF;

      return (
        <DetailCardLink
          icon="download"
          data-testid={`intelligence-agency-pdf-${isSignedPDF === true ? 'download' : 'open'}-${agencyId}`}
          onClick={() => {
            const downloadLink: string | undefined = agency?.properties?.pdf_download_link;
            // Agency links are signed without an existence check, so a stale one fails in its own tab, not over the app.
            window.open(isSignedPDF === true && downloadLink ? downloadLink : raw, '_blank', 'noopener,noreferrer');

            if (project) {
              capturePosthogEvent(POSTHOG_EVENTS.intelligenceAgencyPdfDownloaded, {
                project_id: project.id,
                organization_id: project.organization,
                agency_id: agencyId,
                is_signed: isSignedPDF === true,
              });
            }
          }}
        >
          {getPdfFileName(raw)}
        </DetailCardLink>
      );
    }

    if (property === 'agency_link') {
      const href = toAbsoluteHttpUrl(raw);

      // an unparseable link is still worth showing, just not clickable
      if (!href) {
        return raw;
      }

      return (
        <DetailCardLink
          icon="externalLink"
          href={href}
          target="_blank"
          rel="noreferrer"
          data-testid={`intelligence-agency-link-${agencyId}`}
          onClick={() => {
            if (project) {
              capturePosthogEvent(POSTHOG_EVENTS.intelligenceAgencyLinkOpened, {
                project_id: project.id,
                organization_id: project.organization,
                agency_id: agencyId,
              });
            }
          }}
        >
          {getLinkHost(raw)}
        </DetailCardLink>
      );
    }

    if (property === 'outreach_email') {
      return (
        <DetailCardLink
          icon="envelopeClosed"
          href={`mailto:${raw}`}
          data-testid={`intelligence-agency-email-${agencyId}`}
          onClick={() => {
            if (project) {
              capturePosthogEvent(POSTHOG_EVENTS.intelligenceAgencyEmailClicked, {
                project_id: project.id,
                organization_id: project.organization,
                agency_id: agencyId,
              });
            }
          }}
        >
          {raw}
        </DetailCardLink>
      );
    }

    // outreach_email_name, notes
    return raw;
  };

  const rows: DetailCardRow[] = columns.map((label, i) => {
    const property = columnProperties[i];
    let value: React.ReactNode;

    if (agencyProperties.includes(property)) {
      value = agencyValue(property);
    } else if (property === 'load_date') {
      if (properties?.[property]) {
        const d = new Date(properties[property]);

        if (!Number.isNaN(d.getFullYear())) {
          const age = formatRelativeAge(d);

          value = (
            <span className="inline-flex flex-wrap items-center gap-1">
              <span className="font-normal">{d.toLocaleDateString()}</span>
              {age && <span className="text-orange-60">{age}</span>}
            </span>
          );
        }
      }
    } else if (property === 'route_url') {
      const v = properties?.[property]?.toString()?.trim();

      if (v && v !== NOT_AVAILABLE) {
        const href = toAbsoluteHttpUrl(v);

        // an unparseable link is still worth showing, just not clickable
        value = !href ? (
          v
        ) : (
          <DetailCardLink
            icon="externalLink"
            href={href}
            target="_blank"
            rel="noreferrer"
            data-testid={`intelligence-route-url-${featureId}`}
            onClick={() => {
              if (project) {
                capturePosthogEvent(POSTHOG_EVENTS.intelligenceRouteUrlOpened, {
                  project_id: project.id,
                  organization_id: project.organization,
                  feature_id: featureId,
                });
              }
            }}
          >
            {getLinkHost(v)}
          </DetailCardLink>
        );
      }
    } else if (property === 'projectOverlap') {
      // Handle both 0 (number) and "0" (string) — a truthiness guard treats 0 as
      // missing, which is indistinguishable from no value. PLA-4108.
      const val = properties?.[property];
      if (val != null && val !== '') {
        value = String(val);
      }
    } else {
      const v = properties?.[property]?.toString()?.trim();
      value = v && v !== NOT_AVAILABLE ? v : undefined;
    }

    return { label, value };
  });

  return (
    <DetailCard
      rows={rows}
      className="w-[350px] max-w-[80vw]"
      data-testid={`intelligence-more-info-card-${featureId}`}
    />
  );
}
